import { useEffect } from 'react';
import { useHistory } from "react-router-dom";

/* i18n */
import { useIntl } from 'react-intl';

/* Styles */
import styles from 'styles/recommendations-list.module.scss';
import layoutStyles from 'styles/layout.module.scss';

/* API */
import { rateItem } from 'lib/API';

/* Redux */
import { useSelector, useDispatch } from 'react-redux';
import { setPage } from 'app/slices/careersSlice';
import { setActiveSoc, setPage as setJobsPage } from 'app/slices/jobsSlice';
import { thumbsUpClick, thumbsDownClick, revertThumbsClick } from 'app/slices/ratingsSlice';

/* Analytics */
import { trackEvent } from 'lib/analytics';
import TimeMe from "timeme.js";

/* Material UI and other UI Dependencies */
import Container from '@material-ui/core/Container';
import Typography from "@material-ui/core/Typography";
import withWidth from '@material-ui/core/withWidth';
import Box from '@material-ui/core/Box';
import RecommendationsCard from 'components/RecommendationsCard';
import Button from "@material-ui/core/Button";
import ScrollToTopButton from 'components/ScrollToTopButton';
import { useSnackbar } from 'notistack';

const Careers =() => {

  const dispatch = useDispatch();
  let history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const intl = useIntl();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;
  const currentSoc= useSelector((state) => state.jobs.currentSoc);
  const ratingsLookup = useSelector((state) => state.ratings.results.lookup);
  const results = useSelector((state) => state.careers.results.map(c => {
    if (c.section === 'top' && ratingsLookup[c.id] === undefined) {
      return {...c, flag: 'rateThis'};
    }
    if (c.soc_code === currentSoc) {
      return {...c, flag: 'currentCareer'};
    }
    return c;
  }));

  const goals = ['boostEarnings', 'similarSkills', 'manyJobs'];
  const goal = useSelector((state) => state.careers.goal);
  const userGoal = goals.includes(goal) ? goal : 'boostEarnings';

  const responses = results.reduce((prev, curr) => {    
    return prev[curr.section]
      ? { ...prev, [curr.section]: prev[curr.section].concat(curr)}
      : prev;
  }, { top: [], goal: [], })
  const more = results.filter(r => r.section === 'more');

  const { pageSize, page } = useSelector((state) => state.careers);
  const nPages = Math.ceil(more.length / pageSize);

  const showMore = () => {
    trackEvent('CAREERS_MORE');
    dispatch(setPage(page + 1));
  }

  const showJobs = ({ soc_code }) => {
    dispatch(setActiveSoc(soc_code));
    dispatch(setJobsPage(0));
    history.push(`${urlPrefix}/jobs`);
    if (window) {
      window.scrollTo({top: 0});
    }
  }

  useEffect(() => {
    TimeMe.stopTimer();
    TimeMe.setCurrentPageName("CAREERS");
    TimeMe.startTimer();
    return () => {
      console.log('Leaving Careers')
    };
  }, []);

  const handleShowJobsClick = (groupIndex, item) => () => {
    const eventName = groupIndex === 0
      ? 'CAREERS_TOPCAREER_SEEJOBS'
      : 'CAREERS_GOAL_SEEJOBS';

    trackEvent(eventName, item);
    showJobs(item);
  }

  const handleLikeClick = (groupIndex, item) => () => {
    let previousRating = ratingsLookup[item.id];
    dispatch(thumbsUpClick({ ...item, rating: previousRating }));
    rateItem(item.id, 'liked', item, previousRating === 'liked').catch(() => {
      // We revert the UI changes in case of error
      dispatch(revertThumbsClick({ ...item, rating: previousRating }));
      enqueueSnackbar(intl.formatMessage({id:'app.error'}), {variant: 'error'});
    });
    let eventName = 'CAREERS_MORE_THUMBSUP';

    if (groupIndex !== 'MORE') {
      eventName = groupIndex === 0
      ? 'CAREERS_TOPCAREER_THUMBSUP'
      : 'CAREERS_GOAL_THUMBSUP';
    }    
    trackEvent(eventName, item);
  }

  const handleDislikeClick = (groupIndex, item) => () => {
    let previousRating = ratingsLookup[item.id];
    dispatch(thumbsDownClick({ ...item, rating: previousRating }));
    rateItem(item.id, 'disliked', item, previousRating === 'disliked').catch(() => {
      // We revert the UI changes in case of error
      dispatch(revertThumbsClick({ ...item, rating: previousRating }));
      enqueueSnackbar(intl.formatMessage({id:'app.error'}), {variant: 'error'});
    });
    let eventName = 'CAREERS_MORE_THUMBSDOWN';

    if (groupIndex !== 'MORE') {
      eventName = groupIndex === 0
      ? 'CAREERS_TOPCAREER_THUMBSDOWN'
      : 'CAREERS_GOAL_THUMBSDOWN';
    }
    trackEvent(eventName, item);
  }

  const handleTitleClick = (groupIndex, item) => () => {
    let eventName = 'CAREERS_MORE_MOREINFO';

    if (groupIndex !== 'MORE') {
      eventName = groupIndex === 0
      ? 'CAREERS_TOPCAREER_MOREINFO'
      : 'CAREERS_GOAL_MOREINFO';
    }

    trackEvent(eventName, item);
  }

  const handleTagClick = (groupIndex, item) => () => {
    let eventName = 'CAREERS_MORE_CHICLET';

    if (groupIndex !== 'MORE') {
      eventName = groupIndex === 0
      ? 'CAREERS_TOPCAREER_CHICLET'
      : 'CAREERS_GOAL_CHICLET';
    }

    trackEvent(eventName, item);
  }

  const GROUP_INDEX_GOAL = 1;
  const GROUP_INDEX_MORE = 'MORE';
  return(
    <>
      <div className={styles.content}>
        <Container maxWidth="lg">
          <div className={styles.header__content}>
            <Typography variant="h1" component="h2" gutterBottom>
              {intl.formatMessage(
                {id:'careers.title', description:'string'}
              )}
            </Typography>
            <hr className={layoutStyles.separator} />
            <Box m={4} />
            <Typography variant="h3" gutterBottom>
              {intl.formatMessage(
                {id: 'careers.section.topCareer', description:'string'}
              )}
            </Typography>
            <Box m={3} />
            {responses['top'] && responses['top'].map((item) => (
              <RecommendationsCard
                type="career"
                item={item}
                rating={ratingsLookup[item.id]}
                onButtonClick={handleShowJobsClick(GROUP_INDEX_GOAL, item)}
                onLikeClick={handleLikeClick(GROUP_INDEX_GOAL, item)}
                onDislikeClick={handleDislikeClick(GROUP_INDEX_GOAL, item)}
                onTitleClick={handleTitleClick(GROUP_INDEX_GOAL, item)}
                onTagClick={handleTagClick(GROUP_INDEX_GOAL, item)}
                titleHoverable={true}
                rangeLabel={intl.formatMessage(
                  {id:'careers.salaryRange', description:'string'}
              )}/>
            ))}
            <>
              <hr className={layoutStyles.separator} />
              <Box m={4} />
              <Typography variant="h3" gutterBottom>
                {intl.formatMessage(
                  {id: `careers.section.${userGoal}`}
                )}
                </Typography>
                <Box m={3} />
            </>
            {responses['goal'] && responses['goal'].map((item) => (
              <RecommendationsCard
                type="career"
                item={item}
                rating={ratingsLookup[item.id]}
                onButtonClick={handleShowJobsClick(GROUP_INDEX_GOAL, item)}
                onLikeClick={handleLikeClick(GROUP_INDEX_GOAL, item)}
                onDislikeClick={handleDislikeClick(GROUP_INDEX_GOAL, item)}
                onTitleClick={handleTitleClick(GROUP_INDEX_GOAL, item)}
                onTagClick={handleTagClick(GROUP_INDEX_GOAL, item)}
                titleHoverable={true}
                rangeLabel={intl.formatMessage(
                  {id:'careers.salaryRange', description:'string'}
              )}/>
            ))}
            {more.length > 0 && page > 0 && (
              <>
                <hr className={layoutStyles.separator} />
                <Box m={4} />
                <Typography variant="h3" gutterBottom>
                  {intl.formatMessage(
                    {id: `careers.section.moreCareers`}
                  )}
                </Typography>
                <Box m={3} />
                {more.slice(0, pageSize * page).map(item => (
                  <RecommendationsCard
                    type="career"
                    item={item}
                    rating={ratingsLookup[item.id]}
                    onLikeClick={handleLikeClick(GROUP_INDEX_MORE, item)}
                    onDislikeClick={handleDislikeClick(GROUP_INDEX_MORE, item)}
                    onTitleClick={handleTitleClick(GROUP_INDEX_MORE, item)}
                    onTagClick={handleTagClick(GROUP_INDEX_MORE, item)}
                    titleHoverable={true}
                    onButtonClick={() => showJobs(item)}
                    rangeLabel={intl.formatMessage(
                    {id:'careers.salaryRange', description:'string'}
                  )}/>
                ))}
              </>
            )}
            <div className={styles.bottomBar}>
              <span className={styles.backToTopPlaceholder} />
              {more.length > 0 && page < nPages ? (
                  <Button
                  color="primary"
                  size="large"
                  type="submit"
                  variant="contained"
                  onClick={showMore}
                >
                  {intl.formatMessage(
                    {id:'recommendations.showMore', description:'string'}
                  )}
                </Button> 
              ) : (
                <span />
              )}
              <ScrollToTopButton toTopEvent="CAREERS_TOP" />
            </div>    
          </div>
        </Container>
      </div>
    </>
  )
}

export default withWidth()(Careers);